import React, { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { ImagePlus, Save, Trash2 } from 'lucide-react';
import { db, storage } from '../../firebase';
import { normalizeProductImageFocus } from '../../data/products';
import {
  DEFAULT_PRODUCT_PAGE_SETTINGS,
  normalizeProductPageSettings,
} from '../../hooks/useProductPageSettings';
import { useToast } from '../../hooks/useToast';

const getImageUploadErrorMessage = (error) => {
  const code = String(error?.code || '');
  if (code === 'storage/unauthorized' || code === 'storage/unauthenticated') {
    return 'Nincs jogosultságod a háttérkép feltöltéséhez.';
  }
  return 'Nem sikerült feltölteni a háttérképet.';
};

export function ProductPageBackgroundSettings({ onDirtyChange }) {
  const { toast } = useToast();
  const [form, setForm] = useState(DEFAULT_PRODUCT_PAGE_SETTINGS);
  const [baseline, setBaseline] = useState(DEFAULT_PRODUCT_PAGE_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(baseline),
    [baseline, form],
  );

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'siteSettings', 'productPage'),
      (snapshot) => {
        const next = normalizeProductPageSettings(snapshot.exists() ? snapshot.data() : null);
        setForm(next);
        setBaseline(next);
        setLoading(false);
        setError('');
      },
      () => {
        setLoading(false);
        setError('Nem sikerült betölteni a közös termékoldal-hátteret.');
      },
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  const handleFocusChange = (axis, value) => {
    setForm((previous) => {
      const nextX = axis === 'backgroundFocusX' ? value : previous.backgroundFocusX;
      const nextY = axis === 'backgroundFocusY' ? value : previous.backgroundFocusY;
      const { x, y } = normalizeProductImageFocus(nextX, nextY);
      return {
        ...previous,
        backgroundFocusX: x,
        backgroundFocusY: y,
      };
    });
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
      const imageRef = ref(
        storage,
        `products/_shared/background/${Date.now()}-${safeName}`,
      );
      const snapshot = await uploadBytes(imageRef, file);
      const backgroundImage = await getDownloadURL(snapshot.ref);
      setForm((previous) => ({ ...previous, backgroundImage }));
    } catch (uploadError) {
      console.error('Product page background upload failed:', uploadError);
      setError(getImageUploadErrorMessage(uploadError));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const next = normalizeProductPageSettings(form);
      await setDoc(
        doc(db, 'siteSettings', 'productPage'),
        { ...next, updatedAt: serverTimestamp() },
        { merge: true },
      );
      setForm(next);
      setBaseline(next);
      toast({
        title: 'Háttérkép mentve',
        description: 'A beállítás minden termék részletes oldalán megjelenik.',
      });
    } catch (saveError) {
      console.error('Product page background save failed:', saveError);
      setError('Nem sikerült elmenteni a közös termékoldal-hátteret.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-2xl border border-border/70 bg-card p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-semibold">Közös termékoldal-háttér</h2>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-muted-foreground">
            Ezt az egy képet használja minden termék részletes oldala. A halvány fedőréteg
            biztosítja, hogy a termékinformációk olvashatók maradjanak.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            id="shared-product-page-background"
            type="file"
            accept="image/*"
            onChange={handleUpload}
            className="sr-only"
          />
          <label
            htmlFor="shared-product-page-background"
            className="btn-secondary inline-flex cursor-pointer items-center gap-2 text-xs"
          >
            <ImagePlus className="h-4 w-4" />
            {form.backgroundImage ? 'Kép cseréje' : 'Kép feltöltése'}
          </label>
          {form.backgroundImage ? (
            <button
              type="button"
              onClick={() => setForm((previous) => ({ ...previous, backgroundImage: '' }))}
              className="btn-secondary inline-flex items-center gap-2 text-xs text-destructive"
            >
              <Trash2 className="h-4 w-4" />
              Eltávolítás
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || uploading || saving || loading}
            className="btn-primary inline-flex items-center gap-2 text-xs disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Mentés...' : 'Beállítás mentése'}
          </button>
        </div>
      </div>

      {form.backgroundImage ? (
        <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(15rem,1fr)] lg:items-center">
          <div className="relative aspect-[16/5] overflow-hidden rounded-xl border border-border/70 bg-background">
            <img
              src={form.backgroundImage}
              alt=""
              className="h-full w-full object-cover"
              style={{ objectPosition: `${form.backgroundFocusX}% ${form.backgroundFocusY}%` }}
            />
            <div className="absolute inset-0 bg-background/75" aria-hidden="true" />
            <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-foreground/75">
              Előnézet a termékoldali fedőréteggel
            </span>
          </div>
          <div className="space-y-4">
            <div>
              <div className="mb-1 flex items-center justify-between text-xs">
                <label htmlFor="shared-background-focus-x" className="font-medium">Vízszintes fókusz</label>
                <span>{form.backgroundFocusX}%</span>
              </div>
              <input
                id="shared-background-focus-x"
                type="range"
                min="0"
                max="100"
                value={form.backgroundFocusX}
                onChange={(event) => handleFocusChange('backgroundFocusX', event.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between text-xs">
                <label htmlFor="shared-background-focus-y" className="font-medium">Függőleges fókusz</label>
                <span>{form.backgroundFocusY}%</span>
              </div>
              <input
                id="shared-background-focus-y"
                type="range"
                min="0"
                max="100"
                value={form.backgroundFocusY}
                onChange={(event) => handleFocusChange('backgroundFocusY', event.target.value)}
                className="w-full"
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-5 rounded-xl border border-dashed border-border bg-secondary/25 p-5 text-center text-xs text-muted-foreground">
          Nincs közös háttérkép beállítva.
        </div>
      )}

      {uploading ? <p className="mt-3 text-xs text-muted-foreground">Feltöltés...</p> : null}
      {error ? <p className="mt-3 text-xs text-destructive">{error}</p> : null}
    </section>
  );
}
